export interface TemplateChoiceOption {
  template_guid: string;
  title: string;
  description?: string;
  when_to_use?: string;
  scope?: string;
}

export interface ActiveTemplateChoices {
  question: string;
  allowNone: boolean;
  templates: TemplateChoiceOption[];
}

export interface TemplateChoiceSelection {
  none: boolean;
  template_guid?: string;
  title?: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object'
);

const stringValue = (value: unknown): string | null => (
  typeof value === 'string' ? value : null
);

export const normalizeTemplateChoiceOption = (rawTemplate: unknown): TemplateChoiceOption | null => {
  if (!isRecord(rawTemplate)) return null;

  const templateGuid = stringValue(rawTemplate.template_guid)?.trim() || '';
  const title = stringValue(rawTemplate.title)?.trim() || '';
  if (!templateGuid || !title) return null;

  const description = stringValue(rawTemplate.description)?.trim() || '';
  const whenToUse = stringValue(rawTemplate.when_to_use)?.trim() || '';
  const scope = stringValue(rawTemplate.scope)?.trim() || '';

  return {
    template_guid: templateGuid,
    title,
    ...(description ? { description } : {}),
    ...(whenToUse ? { when_to_use: whenToUse } : {}),
    ...(scope ? { scope } : {}),
  };
};

export const normalizeTemplateChoices = (rawTemplates: unknown): TemplateChoiceOption[] => {
  if (!Array.isArray(rawTemplates)) return [];

  const templates: TemplateChoiceOption[] = [];
  const seenGuids = new Set<string>();

  for (const rawTemplate of rawTemplates) {
    const template = normalizeTemplateChoiceOption(rawTemplate);
    if (!template || seenGuids.has(template.template_guid)) continue;

    seenGuids.add(template.template_guid);
    templates.push(template);
    if (templates.length >= 6) break;
  }

  return templates;
};

export const templateChoicesFromMetadata = (metadata: unknown): ActiveTemplateChoices | null => {
  if (!isRecord(metadata)) return null;

  const rawChoices = isRecord(metadata.template_choices) ? metadata.template_choices : null;
  if (!rawChoices) return null;

  const templates = normalizeTemplateChoices(rawChoices.templates);
  if (templates.length === 0) return null;

  const question = stringValue(rawChoices.question)?.trim() || 'Which template should I use?';
  return {
    question,
    allowNone: rawChoices.allow_none !== false,
    templates,
  };
};

export const templateChoicesFromMessage = (
  message: { message_metadata?: unknown },
): ActiveTemplateChoices | null => templateChoicesFromMetadata(message.message_metadata);

export const templateChoiceFromMetadata = (metadata: unknown): TemplateChoiceSelection | null => {
  if (!isRecord(metadata)) return null;

  const rawChoice = isRecord(metadata.template_choice) ? metadata.template_choice : null;
  if (!rawChoice) return null;

  if (rawChoice.none === true) {
    return { none: true };
  }

  const templateGuid = stringValue(rawChoice.template_guid)?.trim() || '';
  if (!templateGuid) return null;

  const title = stringValue(rawChoice.title)?.trim() || '';
  return {
    none: false,
    template_guid: templateGuid,
    ...(title ? { title } : {}),
  };
};

export const templateChoiceSubmitPayload = (
  template: TemplateChoiceOption | null,
): { content: string; selection: TemplateChoiceSelection } => {
  if (!template) {
    return {
      content: 'Draft without a template',
      selection: { none: true },
    };
  }

  return {
    content: `Use template: ${template.title}\ntemplate_guid: ${template.template_guid}`,
    selection: {
      none: false,
      template_guid: template.template_guid,
      title: template.title,
    },
  };
};
